import { SxProps, TypographyProps, useMediaQuery, useTheme } from '@mui/material';
import { ContentTypeRichText as TContentTypeRichText, Maybe } from '@/types/generated';
import ContentTypeRichText from '../ContentTypeRichText';
import { getWrapperStyles } from './Disclaimer.styles';
import { TemplateSize } from '@shared-components/types';
export interface IDisclaimer {
  /**
   * Data for ContentTypeRichText template. It is an alias of disclaimer field.
   *
   * @type {Maybe<TContentTypeRichText>}
   * @memberof IDisclaimer
   */
  disclaimer?: Maybe<TContentTypeRichText>;
  /**
   * Specifies the font size of the disclaimer text content. Can be Standard, Medium or Large.
   *
   * @type {Maybe<TemplateSize>}
   * @memberof IDisclaimer
   * @default 'Standard'
   * @remarks Consider removing this. Size does not exist on the disclaimerV2 model.
   */
  size?: Maybe<string>;
  /**
   * Width of the Disclaimer template. Can be Full or Inset.
   *
   * @type {Maybe<TemplateWidth>}
   * @memberof IDisclaimer
   * @default 'Full'
   * @deprecated Retained for backwards compatibility. Use disclaimer.contentMaxWidth instead.
   */
  templateWidth?: Maybe<string>;
  /**
   * Optional wrapper styles. Useful for adding margins or padding
   *
   * @type {SxProps}
   * @memberof IDisclaimer
   */
  wrapperSx?: SxProps;
  /**
   * Optional data-testid attribute for testing purposes
   *
   * @type {string}
   * @memberof IDisclaimer
   */
  dataTestId?: string;
  /**
   * Disables the default side padding of the Container component.
   *
   * @type {boolean}
   * @memberof IDisclaimer
   */
  disableGutters?: boolean;
  /**
   * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
   *
   * @type {Maybe<string>}
   * @memberof IDisclaimer
   */
  anchorId?: Maybe<string>;
}
function resolveTypographyVariant(size: TemplateSize, isSmallScreen: boolean): TypographyProps['variant'] {
  switch (size) {
    case 'Standard':
      return 'lowestLevelBook';
    case 'Medium':
      return isSmallScreen ? 'bodySmallBook' : 'bodyLargeBook';
    case 'Large':
      return isSmallScreen ? 'bodySmallBook' : 'bodyXLBook';
    default:
      return 'lowestLevelBook';
  }
}
export default function Disclaimer({
  disclaimer,
  size,
  wrapperSx = {},
  dataTestId = 'section_disclaimer',
  disableGutters = false,
  anchorId
}: Readonly<IDisclaimer>) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (!disclaimer?.content) return null;
  const typographyVariant = resolveTypographyVariant(size as TemplateSize || 'Standard', isSmallScreen);
  const containerWidth = disclaimer.contentMaxWidth || 'none';
  return <ContentTypeRichText {...disclaimer} dataTestId={dataTestId} disablePaddingOn={['Top', 'Bottom']} disableGutters={disableGutters} contentMaxWidth={'none'} containerSx={getWrapperStyles(wrapperSx, containerWidth)} docProps={{
    variant: typographyVariant,
    color: 'text.secondary'
  }} anchorId={anchorId} data-sentry-element="ContentTypeRichText" data-sentry-component="Disclaimer" data-sentry-source-file="Disclaimer.tsx" />;
}